import { toCalendarDate } from "@internationalized/date";
import { TimedFormDuration } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { ComponentProps } from "react";
import { FieldPath, FieldValues, useFormContext } from "react-hook-form";
import { DateRangePicker, Field, Flex } from "shared/components/Spor";
import { EndsWith } from "shared/utils/objectUtils";

type DateRangePickerProps = Omit<
  ComponentProps<typeof DateRangePicker>,
  "variant"
>;
export type DateTimeRangePickerProps<T extends FieldValues> =
  DateRangePickerProps & {
    durationsField: FieldPath<T> & EndsWith<"durations">;
  };

export const DateTimeRangePicker = <T extends FieldValues>({
  durationsField: _durationsField,
  ...props
}: DateTimeRangePickerProps<T>) => {
  /**
   * Zod doesnt understand non-const pathes that well, so we
   * trick it to think durationsField is a const "durations".
   */
  const durationsField = _durationsField as "durations";
  type FormWithDuration = { [durationsField]: TimedFormDuration };

  const { setValue, watch } = useFormContext<FormWithDuration>();

  const fromTimeField = `${durationsField}.from_time` as const;
  const toTimeField = `${durationsField}.to_time` as const;

  const durations = watch(durationsField);
  const fromTime = watch(fromTimeField);
  const toTime = watch(toTimeField);

  if (!durations) return null; // Wait for defaultForm useEffect

  // Keep this as a fragment as this component is reused in DateRangeAndSearch
  return (
    <Field>
      <Flex>
        <DateRangePicker
          variant="core"
          startLabel="Startdato"
          endLabel="Sluttdato"
          aria-label="Dato"
          value={{
            start: toCalendarDate(fromTime),
            end: toCalendarDate(toTime),
          }}
          onValueChange={(date) => {
            if (date) {
              setValue(fromTimeField, fromTime.set({ ...date.start }));
              setValue(toTimeField, toTime.set({ ...date.end }));
            }
          }}
          {...props}
        />
      </Flex>
    </Field>
  );
};
